import {
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  Optional,
} from "@angular/core";
import { UntypedFormControl } from "@angular/forms";
import { MatDialogRef } from "@angular/material/dialog";
import { finalize, Subscription } from "rxjs";
import { NotificationService } from "src/app/services/notification.service";
import { extractApiErrorMessage } from "src/app/shared/utils/extract-api-error-message.util";
import {
  Product,
  ProductMergeCard,
  ProductMergePreview,
  RemoveDouble,
} from "src/typings";
import { ProductService } from "../product.service";

type Candidate = Pick<Product, "id" | "article" | "description"> & {
  brand?: { description?: string } | null;
};

@Component({
  selector: "app-remove-double",
  templateUrl: "./remove-double.component.html",
  styleUrls: ["./remove-double.component.scss"],
})
export class RemoveDoubleComponent implements OnDestroy {
  loading = false;
  searching = false;
  preview: ProductMergePreview | null = null;
  results: ProductMergeCard[] = [];
  error = "";
  searchError = "";
  searchControl = new UntypedFormControl("");
  idControl = new UntypedFormControl();
  newPriceControl = new UntypedFormControl(false);
  saveDescription = new UntypedFormControl(false);
  identityControl = new UntypedFormControl(false);
  reasonControl = new UntypedFormControl("Дублирующие карточки товара");
  doneEvent = new EventEmitter<boolean>();
  private subscriptions = new Subscription();
  private selectedCandidateIds = new Set<number>();
  private _product: Product | undefined;
  private _candidates: Candidate[] = [];
  private revision = 0;
  private operationId = "";

  @Input() set product(value: Product | undefined) {
    this._product = value;
    this.invalidate();
  }
  get product() {
    return this._product;
  }
  @Input() set candidates(value: Candidate[] | undefined) {
    this._candidates = (value || []).filter(
      (c, i, list) => c?.id && list.findIndex((r) => r.id === c.id) === i
    );
    this.selectedCandidateIds = new Set(
      this._candidates.map((c) => Number(c.id))
    );
    this.selectedCandidateIds.delete(this.normalizedMainId);
    this.invalidate();
  }
  get candidates() {
    return this._candidates;
  }
  get mergeCandidates() {
    return this._candidates;
  }
  get hasMultipleCandidates() {
    return this._candidates.length > 1;
  }
  get normalizedMainId() {
    return Number(this.idControl.value);
  }

  constructor(
    private productService: ProductService,
    private notificationService: NotificationService,
    @Optional() private dialogRef?: MatDialogRef<RemoveDoubleComponent>
  ) {
    for (const control of [
      this.idControl,
      this.newPriceControl,
      this.saveDescription,
      this.reasonControl,
    ]) {
      this.subscriptions.add(
        control.valueChanges.subscribe(() => this.invalidate())
      );
    }
    this.dialogRef?.updateSize("720px");
  }
  ngOnDestroy() {
    this.subscriptions.unsubscribe();
  }
  private invalidate() {
    this.revision++;
    this.preview = null;
    this.error = "";
    this.identityControl.setValue(false, { emitEvent: false });
    this.operationId = "";
  }
  isCandidateSelected(id: number) {
    return this.selectedCandidateIds.has(Number(id));
  }
  toggleCandidate(id: number, checked: boolean) {
    if (this.loading) return;
    if (checked) this.selectedCandidateIds.add(Number(id));
    else this.selectedCandidateIds.delete(Number(id));
    this.invalidate();
  }
  choose(card: ProductMergeCard) {
    if (this.loading) return;
    this.idControl.setValue(card.id);
    this.results = [];
  }
  search() {
    if (this.searching || this.loading) return;
    const term = String(this.searchControl.value || "").trim();
    if (term.length < 2) {
      this.searchError = "Введите хотя бы 2 символа.";
      return;
    }
    this.searching = true;
    this.searchError = "";
    this.subscriptions.add(
      this.productService
        .mergeCandidates(term)
        .pipe(finalize(() => (this.searching = false)))
        .subscribe({
          next: (cards) => {
            this.results = cards.filter(
              (c) => this.hasMultipleCandidates || c.id !== this.product?.id
            );
            if (!this.results.length)
              this.searchError =
                "Карточки не найдены. Попробуйте артикул или ID.";
          },
          error: (error) => {
            this.searchError = extractApiErrorMessage(
              error,
              "Не удалось найти карточки."
            );
          },
        })
    );
  }
  private payload(): RemoveDouble | null {
    const main = this.normalizedMainId;
    if (!Number.isSafeInteger(main) || main <= 0) {
      this.error = "Выберите основную карточку или укажите её ID.";
      return null;
    }
    const ids = this.hasMultipleCandidates
      ? [...this.selectedCandidateIds].filter((id) => id !== main)
      : [Number(this.product?.id)].filter((id) => id > 0 && id !== main);
    if (!ids.length) {
      this.error =
        "Выберите хотя бы один дубль, отличный от основной карточки.";
      return null;
    }
    return {
      main_id: main,
      another_ids: ids,
      new_price:
        !this.hasMultipleCandidates && this.newPriceControl.value === true,
      save_description:
        !this.hasMultipleCandidates && this.saveDescription.value === true,
      reason: String(this.reasonControl.value || "").trim(),
    };
  }
  private busy(value: boolean) {
    this.loading = value;
    if (this.dialogRef) this.dialogRef.disableClose = value;
    for (const control of [
      this.idControl,
      this.searchControl,
      this.newPriceControl,
      this.saveDescription,
      this.reasonControl,
    ]) {
      if (value) control.disable({ emitEvent: false });
      else control.enable({ emitEvent: false });
    }
  }
  check() {
    if (this.loading) return;
    this.invalidate();
    const payload = this.payload();
    if (!payload) return;
    const revision = this.revision;
    this.busy(true);
    this.subscriptions.add(
      this.productService
        .previewMerge(payload)
        .pipe(finalize(() => this.busy(false)))
        .subscribe({
          next: (preview) => {
            if (revision !== this.revision) return;
            this.preview = preview;
            this.operationId = crypto.randomUUID();
          },
          error: (error) => {
            this.error = extractApiErrorMessage(
              error,
              "Не удалось проверить слияние."
            );
          },
        })
    );
  }
  send() {
    if (
      this.loading ||
      !this.preview ||
      this.preview.blockers.length ||
      this.identityControl.value !== true
    )
      return;
    const payload = this.payload();
    if (!payload) return;
    this.busy(true);
    this.error = "";
    this.subscriptions.add(
      this.productService
        .removeDouble({
          ...payload,
          preview_token: this.preview.preview_token,
          operation_id: this.operationId,
          confirmed_identity: true,
        })
        .pipe(finalize(() => this.busy(false)))
        .subscribe({
          next: (result) => {
            if (!result?.merged_count) {
              this.error =
                "Ни одна карточка не была объединена. Обновите проверку.";
              return;
            }
            this.notificationService.notice(
              `Объединено дублей: ${result.merged_count}. Основная карточка #${result.main_id}.`
            );
            this.doneEvent.emit(true);
          },
          error: (error) => {
            this.error = extractApiErrorMessage(
              error,
              "Не удалось завершить слияние."
            );
          },
        })
    );
  }
  cancel() {
    if (!this.loading) this.dialogRef?.close();
  }
}
